import React, {useEffect, useState} from 'react';
import FormsyForm from 'epic-diesel-form/src/components/external/FormsyForm';
import RadioBox from 'epic-diesel-form/src/components/form/RadioBox';
import cx from 'classnames';
import {OAuthViewProps} from './OAuthView';
import {parse} from 'query-string';
import {ChannelStatus, ErrorCode, SocialPlatform, AuthedPage} from '../../../shared/types';
import {OAuth as OAuthState} from '../../reducers/oauth.reducer';
import {Message, Icon, svgs, Button, buttonTypes, priority} from 'epic-diesel-common';
import {sortArrayByKey} from '../../utils/Utils';
import {transformViolation, mapViolation} from '../../../shared/utils/SocialUtils';
import Tooltip from '../../components/common/Tooltip';

//@ts-ignore
import styles from './OAuth.pcss';
import {dispatchAuthResponseEvent} from '../../utils/oauth';

interface State {
    platform: SocialPlatform;
    state: string;
    errors?: string;
    oauthState?: OAuthState;
    selectedPage: AuthedPage;
    selectedChannelId: string;
    showError: boolean;
}

const initialState: State = {
    platform: null,
    state: null,
    errors: null,
    selectedPage: null,
    selectedChannelId: null,
    showError: false
};

const CODES = {
    cancel: 'epic.affiliate.common.cancel',
    submit: 'epic.affiliate.common.submit',
    selectPage: 'epic.affiliate.social.channels.selectPage.v2',
    selectPageHeader: 'epic.affiliate.social.channels.selectPage.header.v2',
    selectOne: 'epic.affiliate.social.channels.errors.selectOne.v2',
    programReqs: 'epic.affiliate.social.channels.errors.programReqs.v2',
    checkPerms: 'epic.affiliate.social.channels.errors.checkPermissions.v2',
    noPages: 'epic.affiliate.social.channels.errors.pageNotFound.v2',
    checkErrors: 'epic.affiliate.common.checkErrors.v2'
};

const OAuth: React.FC<OAuthViewProps> = props => {
    const [state, setState] = useState(() => initialState);
    const {loading, oauth} = props.oauthState || {};

    const updateState = data => {
        setState({...state, ...data});
    };

    const dispatch = () => {
        if (!state.selectedChannelId) return;
        const {violations: errors = [], oauthDetails} = state.selectedPage || {};
        const {valid} = oauthDetails || {};
        const {state: authState} = oauth[state.platform] || {};
        dispatchAuthResponseEvent({...state.selectedPage, errors, valid, authState});
        props.closePopup();
    };

    const {affiliateAccount} = props.influencerState;
    const {socialChannels = []} = affiliateAccount || {};
    const socialChannel = socialChannels.find(sch => sch.platform === state.platform) || {};

    useEffect(() => {
        const {search} = props.location || {};
        const parsedQuery = (parse(search, {decode: true}) as unknown) as State;
        updateState(parsedQuery);
        props.getOAuth(parsedQuery);
    }, []);

    useEffect(() => {
        if (state.selectedChannelId) {
            dispatch();
        }
    }, [socialChannel.channelStatus]);

    const renderErrorMessage = (code: string = ErrorCode.SOCIAL_CHANNEL_SOMETHING_WRONG) => {
        const error = transformViolation(code, state.platform, props.configState);
        let messageText;

        if (typeof error === 'object') {
            messageText = <Message code={error.errorCode} args={error.messageVars} />;
        } else {
            messageText = <Message code={error} />;
        }

        return (
            <li key={code} className={styles.errorMessage}>
                {messageText}
            </li>
        );
    };

    const renderViolationTip = page => {
        const msgCodes = mapViolation(page, true);
        const authStatusText = msgCodes.map(error => renderErrorMessage(error));
        return (
            <Tooltip
                className={styles.tip}
                placement="top"
                title={
                    <>
                        <Message code={CODES.checkErrors} />
                        <ul className={styles.validationData}>{authStatusText}</ul>
                    </>
                }>
                <Icon icon={svgs.INFO} fontSize="small" />
            </Tooltip>
        );
    };

    const onSubmit = () => {
        if (state.showError || loading) return;
        if (socialChannel.channelStatus === ChannelStatus.VALID) {
            return dispatch();
        }
        props.updateOAuthChannel(state.platform, state.selectedChannelId);
    };

    const onInvalidSubmit = () => {
        let hasError = false;
        if (!state.selectedChannelId || !state || !(state.platform in SocialPlatform)) {
            hasError = true;
        }
        updateState({hasError});
        return hasError;
    };

    const {platform} = state || {};
    const {pages} = oauth[platform] || {};

    if (!platform || !pages || !pages.length) {
        return null;
    }

    const sortedPages = sortArrayByKey(pages, 'channelStatus', false);
    const enableSubmitBtn = state.selectedChannelId && !loading;
    return (
        <div className={styles.wrapper}>
            <FormsyForm invalidSubmit={onInvalidSubmit} submitForm={onSubmit} callbackDelay={2000}>
                <div className={styles.contentContainer}>
                    <div className={styles.imageHeader}>
                        <Icon icon={svgs.EPIC} className={styles.icon} fontSize="large" />
                    </div>
                    <h6>
                        <Message code={CODES.selectPageHeader} />
                    </h6>
                    <div className={styles.list}>
                        {sortedPages.map((page, i) => {
                            const {
                                link,
                                channelStatus,
                                channelId,
                                violations = [],
                                oauthDetails
                            } = page;
                            const {valid} = oauthDetails || {};
                            const inEligible =
                                !valid ||
                                channelStatus !== ChannelStatus.VALID ||
                                violations.length > 0;
                            const isSelected = channelId === state.selectedChannelId;
                            const classes = cx(styles.listItem, {
                                [styles.disabled]: inEligible
                            });

                            return (
                                <RadioBox
                                    key={`page-${Math.random() + i}`}
                                    disabled={inEligible}
                                    className={classes}
                                    name={channelId || link}
                                    value={isSelected}
                                    onChange={(name, value) => {
                                        updateState({
                                            selectedChannelId: name,
                                            selectedPage: page
                                        });
                                    }}>
                                    <div className={styles.radioContent}>
                                        <span className={styles.link}>{link}</span>
                                        {inEligible && renderViolationTip(page)}
                                    </div>
                                </RadioBox>
                            );
                        })}
                    </div>
                </div>

                <div key="SubmitSection" className={styles.submitSection}>
                    {state.showError && (
                        <div className={styles.validationData}>
                            {renderErrorMessage(CODES.selectOne)}
                            {renderErrorMessage(CODES.programReqs)}
                            {renderErrorMessage(CODES.checkPerms)}
                        </div>
                    )}

                    <Button priority={priority.SECONDARY} onClick={props.closePopup}>
                        <Message code={CODES.cancel} />
                    </Button>

                    <Button
                        type={buttonTypes.SUBMIT}
                        disabled={!enableSubmitBtn}
                        priority={priority.PRIMARY}
                        theme="dark">
                        <Message code={CODES.submit} />
                    </Button>
                </div>
            </FormsyForm>
        </div>
    );
};

export default OAuth;
